import { createElement, useLayoutEffect, useRef, type CSSProperties, type ReactNode } from "react";
import type { PieceColor, Square } from "@bing/engine-chess";
import type { SfxName } from "../sound/sfx";
import { prefersReducedMotion } from "./card-motion";

/* ------------------------------------------------------------------ *
 * Chess board motions — the pure, testable half of the ChessTable
 * "exclamations" (design spec §11): the piece SLIDE (from→to), the
 * captured-piece VANISH, the castle rook's companion slide, and the
 * SAN→sound mapping. The visual application (`SlidePiece`) mirrors the
 * card tables' `ThrownCard` (WAAPI, mount-once, fill dropped on finish,
 * inert without `Element.animate`), so a happy-dom / SSR runtime places
 * the piece at rest with nothing broken.
 *
 * `prefersReducedMotion` is reused from `card-motion` — ONE source of
 * truth for the OS "reduce motion" preference across every table.
 * ------------------------------------------------------------------ */

export { prefersReducedMotion };

/** Slide timing — a touch faster than a card's flight (a chess move is a short hop,
 *  not a throw across the felt). Well under any pacing beat. */
export const SLIDE_MS = 200;
export const SLIDE_EASING = "cubic-bezier(.2,.7,.3,1)";

/** File/rank (0-based) of an algebraic square: "e4" → { file: 4, rank: 3 }. */
export function squareCoords(sq: Square): { file: number; rank: number } {
  return { file: sq.charCodeAt(0) - 97, rank: Number(sq[1]) - 1 };
}

/**
 * A square's position in DISPLAY space (row/col, 0..7 from the top-left of the
 * rendered board) — this MUST match `ChessTable`'s own orientation mapping: White
 * (`you === 0`) sees rank 1 at the bottom; Black (`you === 1`) sees it flipped.
 */
export function displayPos(sq: Square, you: number): { col: number; row: number } {
  const { file, rank } = squareCoords(sq);
  return you === 0 ? { col: file, row: 7 - rank } : { col: 7 - file, row: rank };
}

/**
 * The offset — IN SQUARES — a mover's piece starts at so it appears to come FROM its
 * origin and slide to rest on its destination. The slide wrapper fills the destination
 * square (`inset:0`), so one square = 100% of the wrapper's own box; the start transform
 * is therefore `translate(dx*100%, dy*100%)` and needs no pixel measurement.
 */
export function slideDelta(from: Square, to: Square, you: number): { dx: number; dy: number } {
  const f = displayPos(from, you);
  const t = displayPos(to, you);
  return { dx: f.col - t.col, dy: f.row - t.row };
}

/** The mover's flight: start at its origin square (offset) → rest at the destination. */
export function slideKeyframes(dx: number, dy: number): Keyframe[] {
  // A subtle lift-and-place: the piece rises (scales up) through the midpoint of its travel and
  // settles onto the target — a more tactile motion than a flat translate, with NO overshoot.
  return [
    { transform: `translate(${dx * 100}%, ${dy * 100}%) scale(1)`, offset: 0 },
    { transform: `translate(${dx * 50}%, ${dy * 50}%) scale(1.08)`, offset: 0.5 },
    { transform: "translate(0%, 0%) scale(1)", offset: 1 },
  ];
}

// ── SAN parsing (drives the sound cue + the capture/castle/promotion motions) ──────

export function sanGivesCheck(san: string): boolean {
  return san.includes("+") || san.includes("#");
}
export function sanIsCastle(san: string): boolean {
  return san.startsWith("O-O");
}
export function sanIsPromotion(san: string): boolean {
  return san.includes("=");
}
export function sanIsCapture(san: string): boolean {
  return san.includes("x");
}

/**
 * The single sound cue for a completed move, by priority: check/mate is the most urgent
 * (its alert wins even over a capture), then castle, promotion, capture, else a quiet
 * move. One cue per move — never a pile-up. Pure so it is unit-tested directly.
 */
export function sanToCue(san: string): SfxName {
  if (sanGivesCheck(san)) return "chessCheck";
  if (sanIsCastle(san)) return "chessCastle";
  if (sanIsPromotion(san)) return "chessPromote";
  if (sanIsCapture(san)) return "chessCapture";
  return "chessMove";
}

/**
 * The rook's companion move when the king castles: a king that jumped two files means
 * the rook hops to the far side of it (kingside h→f / queenside a→d, on the king's rank).
 * Returns `null` for any non-castling move. So the rook can slide WITH the king (§11 #3).
 */
export function deriveCastleRook(
  kingFrom: Square,
  kingTo: Square,
): { from: Square; to: Square } | null {
  const f = squareCoords(kingFrom);
  const t = squareCoords(kingTo);
  if (f.rank !== t.rank) return null;
  const rankChar = kingFrom[1] ?? "1";
  if (t.file - f.file === 2) return { from: `h${rankChar}`, to: `f${rankChar}` }; // O-O
  if (t.file - f.file === -2) return { from: `a${rankChar}`, to: `d${rankChar}` }; // O-O-O
  return null;
}

/**
 * The square whose piece was CAPTURED by a move `from`→`to` (given the SAN and a reader
 * over the PRE-move board). Normal captures take the piece standing on `to`; an en-passant
 * pawn capture takes the pawn on the destination FILE at the origin RANK (that square is
 * empty pre-move, which is exactly how e.p. is detected). Returns `null` for a quiet move.
 */
export function capturedSquareOf(
  from: Square,
  to: Square,
  san: string,
  hadPieceAt: (sq: Square) => boolean,
): Square | null {
  if (!sanIsCapture(san)) return null;
  if (hadPieceAt(to)) return to; // a piece stood on the destination → normal capture
  // No piece on `to` but the SAN says capture → en passant: the taken pawn sat beside
  // the mover, on the destination file at the mover's starting rank.
  const toFile = to[0] ?? "a";
  const fromRank = from[1] ?? "1";
  return `${toFile}${fromRank}`;
}

/** Chess semantics: the army whose piece was captured is the mover's opponent. */
export function capturedColor(moverColor: PieceColor): PieceColor {
  return moverColor === "w" ? "b" : "w";
}

/**
 * A piece that slides in from its origin square (§11 #1/#3/#4). Wrap the piece in this on
 * the DESTINATION square and KEY it by the move, so a fresh move remounts it and the
 * entrance plays exactly once (a settled piece never re-slides on an unrelated re-render —
 * a clock tick, a hover). Mirrors `ThrownCard`: WAAPI with `fill: "both"` in a LAYOUT
 * effect (the start offset is applied before the first paint, so the piece is never
 * glimpsed at rest first), and the fill is dropped the instant it lands (a finished
 * filling transform would trap `position: fixed` descendants — the documented `view-enter`
 * trap). `animate === false`, a zero delta, reduced motion, or a runtime without
 * `Element.animate` all place the piece at rest with no motion.
 *
 * `data-slide-from` records the delta the piece flew from — the entrance's one observable
 * marker (absent when it does not animate).
 */
export function SlidePiece({
  dx,
  dy,
  animate,
  className,
  style,
  children,
}: {
  dx: number;
  dy: number;
  /** Whether to play the slide at all (false on the first mount / a resume). */
  animate: boolean;
  className?: string;
  style?: CSSProperties;
  children: ReactNode;
}): ReactNode {
  const ref = useRef<HTMLSpanElement | null>(null);
  const flying = animate && (dx !== 0 || dy !== 0) && !prefersReducedMotion();

  useLayoutEffect(() => {
    if (!flying) return undefined;
    const el = ref.current;
    if (!el || typeof el.animate !== "function") return undefined;
    const anim = el.animate(slideKeyframes(dx, dy), {
      duration: SLIDE_MS,
      easing: SLIDE_EASING,
      fill: "both",
    });
    anim.onfinish = (): void => anim.cancel();
    return () => anim.cancel();
    // Mount-once (deliberately empty deps), like `ThrownCard`: the entrance plays exactly
    // when the node appears; callers key this wrapper by the move so a fresh move remounts it.
  }, []);

  return createElement(
    "span",
    {
      ref,
      className,
      style,
      ...(flying ? { "data-slide-from": `${dx},${dy}` } : {}),
    },
    children,
  );
}
